import React, { useReducer } from 'react'
import { sessionStorage } from 'react-storage'
import DefaultLayout, {
  LayoutSpacingOptions,
} from '@src/components/DefaultLayout'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import { homeRoute, dnaPlatesRoute, useRouter } from '@src/routes'
import PageHeader from '@src/routes/Configurators/components/PageHeader'
import LeftNav from '@src/routes/Configurators/components/LeftNav'
import { FormattedMessage } from 'react-intl'
import messages from '../../messages'
import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3'
import Step4 from './Step4'
import Step5 from './Step5'

import {
  SpecificationsComponent,
  usePlatesSessionDataQuery,
  useGetProductInformationQuery,
} from '@src/queries/ConfiguratorTubesQuery.generated'
import { useCurrentUserQuery } from '@src/queries/CurrentUserQuery.generated'
import { initialState, reducer, ContextProps } from './Reducer'
import { ActiveLinkTypeDnaplate, PlateProduct } from './enum'
import { default as UUID } from 'uuid'
import { useUserSession } from '@utils/useUserSession'
import { TubeProperties } from '../../utils/enum'
import ConfiguratorPageError from '@src/routes/Configurators/utils/ConfiguratorPageError'
import ErrorAlert from '@src/routes/Configurators/components/ErrorAlert'
import { BreadcrumbNavigationUrls } from '@src/routes/Configurators/utils/BreadcrumbNavigationUrlsEnum'
import {
  ProductInformation,
  DnaAndPepScreenProductDetails,
} from '../ConfiguratorSharedTypes'
import Breadcrumb from '@src/components/Breadcrumb'

const validateActiveLink = (activeLink) => {
  return activeLink === '' || activeLink == null
    ? ActiveLinkTypeDnaplate.SequenceUpload
    : activeLink
}

export interface ConfiguratorAddToMiniCart {
  productType: string
  participantId: string
  countryCode: string
  locale: string
  sapNextEnabled: boolean
  timeZone: string
  store: string
}
export const configContext = React.createContext({} as ContextProps)

const StandardTube = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const router = useRouter()
  const { userSession } = useUserSession()
  const countryCd = userSession.country
  const [activeStep, setActiveStep] = React.useState(0)
  const [open, setOpen] = React.useState(false)
  const [sessionResp, setSessionResp] = React.useState<any>({})
  const [enableOligoConfigurationLink, setEnableOligoConfigurationLink] =
    React.useState(false)
  const [enableSpecificationsLink, setEnableSpecificationsLink] =
    React.useState(false)
  const [enablePackingAndShippingLink, setEnablePackingAndShippingLink] =
    React.useState(false)
  const [enableOrderConfirmationLink, setEnableOrderConfirmationLink] =
    React.useState(false)
  const { data, loading } = useCurrentUserQuery()

  const currentUser: any = data?.me || null
  let loggedIn
  if (!loading)
    loggedIn =
      currentUser && currentUser.__typename === TubeProperties.LoggedInUser

  let participationId: string
  let locale: string
  let isCSR: boolean
  let isCSRSupervisor: boolean
  let isSAPNextEnabled: boolean
  let isOrderCustomer: boolean
  let isOrderableCustomer: boolean
  let isGuestShoppingCountry: boolean
  let isConfiguratorDisabled: boolean
  let accessToken = ''
  let store = ''
  let product = ''
  const date = new Date()
  const timeZone = (date.getTimezoneOffset() / 60) * -1

  if (router !== null && router.query !== null) {
    product = router.query.product
  }

  if (userSession.accessToken) {
    accessToken = userSession.accessToken
  }

  if (userSession.store) {
    store = userSession.store
  }

  if (loggedIn && currentUser) {
    participationId = currentUser.id
    locale = `${userSession.language}_${countryCd}`
    if (locale === '') {
      locale = currentUser?.metadata.locale
    }
    isCSR = currentUser?.roles.isCustomerServiceRep
    isCSRSupervisor = currentUser?.roles.isCustomProductCSR
    isSAPNextEnabled = currentUser.metadata.isSAPNextEnabled
    isGuestShoppingCountry = currentUser.metadata.guestShoppingType
    isOrderCustomer =
      currentUser?.roles.isPublicOrderingCustomer ||
      currentUser?.roles.isQuoteRequester
    if (isGuestShoppingCountry !== null) {
      isOrderableCustomer = true
    } else if (isOrderCustomer && isGuestShoppingCountry === null) {
      isOrderableCustomer = true
    } else {
      isOrderableCustomer = false
    }
    isConfiguratorDisabled =
      currentUser.participantSitePreference?.hideOligoPeptideDesign
  } else if (userSession.accessToken) {
    participationId = userSession.accessToken
    locale = `${userSession.language}_${countryCd}`
    isCSR = false
    isCSRSupervisor = false
    isOrderableCustomer = false
    isSAPNextEnabled = currentUser?.metadata?.isSAPNextEnabled
    isConfiguratorDisabled = false
  } else {
    //TODO: commented participationId as a part of fix for STRAT-11633
    //Identify why the page is going in an infinite loop when fetching the id dynamically.
    participationId = UUID.v4()
    locale = `en_${countryCd}`
    isCSR = false
    isCSRSupervisor = false
    isOrderableCustomer = false
    isSAPNextEnabled = true
    isConfiguratorDisabled = false
  }

  const productDetails: DnaAndPepScreenProductDetails = {
    productType: product,
    participantId: participationId,
    countryCode: countryCd,
    isRegUser: loggedIn,
    locale: locale,
    isCSR: isCSR,
    isCSRSupervisor: isCSRSupervisor,
    timeZone: timeZone.toString(),
    sapNextEnabled: isSAPNextEnabled,
    store: store,
  }
  const productInformation: ProductInformation = {
    productType: product,
    countryCode: countryCd,
    isCsr: isCSR,
    isCsrSupervisor: isCSRSupervisor,
    isOrderableCustomer: isOrderableCustomer,
    regUser: loggedIn,
    locale: locale,
    sapNextEnabled: isSAPNextEnabled,
    hideOligoPeptideDesign: isConfiguratorDisabled,
  }

  const configuratorAddToMiniCart: ConfiguratorAddToMiniCart = {
    productType: product,
    countryCode: countryCd,
    participantId: participationId,
    locale: locale,
    sapNextEnabled: isSAPNextEnabled,
    timeZone: timeZone.toString(),
    store: store,
  }

  if (loggedIn) {
    const referrerParticipantId: string | null = sessionStorage.getItem(
      'CONFIGURATOR_REFERRER_ID'
    )
    if (referrerParticipantId) {
      productDetails.guestParticipantId = referrerParticipantId
    }
  }

  const { data: platesSessionData } = usePlatesSessionDataQuery({
    variables: {
      product: productDetails,
    },
    fetchPolicy: 'no-cache',
    skip: !accessToken,
    ssr: false,
  })

  React.useEffect(() => {
    setSessionResp(platesSessionData)
  }, [platesSessionData])

  //  if productCode  is 1 i.e product is unauthorized
  //                     2 i.e product is invalid
  //                     0 i.e product is valid
  const [productCode, setProductCode] = React.useState(0)
  const [isApiCall, setIsApiCall] = React.useState(true)
  let apiData: any = {}
  const FetchProductDetails = async (obj) => {
    const { data, loading } = useGetProductInformationQuery({
      variables: {
        input: obj,
      },
    })
    if (!loading && loggedIn !== undefined) {
      apiData = data?.getProductInformation
    }
  }
  React.useEffect(() => {
    if (Object.keys(apiData).length !== 0 && isApiCall) {
      setIsApiCall(false)
      const {
        productType,
        shippingOptions,
        brand,
        companionProducts,
        instructions,
        displayName,
        packageType,
        typeCode,
        currencyFormat,
        decimalSeparator,
        thousandSeparator,
        prefixCurrecny,
        thousandSpacing,
      } = apiData

      if (displayName === TubeProperties.UnAuthorized) {
        setProductCode(1)
      } else if (displayName.includes(TubeProperties.InvalidProduct)) {
        setProductCode(2)
      } else if (displayName.includes(TubeProperties.ProductNotValid)) {
        setProductCode(3)
      } else if (displayName.includes(TubeProperties.UserCannotOrder)) {
        setProductCode(4)
      } else {
        setProductCode(0)
        const productData = {
          productType: productType,
          shippingOptions: shippingOptions,
          brand: brand,
          companionProducts: companionProducts,
          instructions: instructions,
          packageType: packageType,
          typeCode: typeCode,
          currencyFormat: currencyFormat,
          decimalSeparator: decimalSeparator,
          thousandSeparator: thousandSeparator,
          prefixCurrecny: prefixCurrecny,
          thousandSpacing: thousandSpacing,
        }
        dispatch({
          type: 'SET_PRODUCT_INFORMATION',
          data: productData,
        })
      }
    }
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData])

  accessToken && FetchProductDetails(productInformation)

  React.useEffect(() => {
    const { activeLink } = router.query
    if (sessionResp && sessionResp?.getSessionDataForPlates) {
      if (sessionResp.getSessionDataForPlates?.data.length !== 0) {
        if (
          sessionResp !== null &&
          sessionResp.getSessionDataForPlates !== null
        ) {
          sessionResp.getSessionDataForPlates.data.forEach((item) => {
            state.stepNo = item.pageNo
            delete item.__typename
            item.wellList.forEach((well) => {
              delete well.__typename
              well.listError.forEach((error) => {
                delete error.__typename
              })
              well.listWarning.forEach((warning) => {
                delete warning.__typename
              })
            })
            item.biTubeWellList.forEach((duplexwell) => {
              delete duplexwell.__typename
              duplexwell.listError.forEach((error) => {
                delete error.__typename
              })
              duplexwell.listWarning.forEach((warning) => {
                delete warning.__typename
              })
            })
          })
          dispatch({
            type: 'UPDATE_INPUT',
            data: sessionResp.getSessionDataForPlates.data,
          })
          dispatch({
            type: 'UPDATE_CURRENT_PLATE_INDEX',
            data: 0,
          })
          dispatch({
            type: 'IS_PAGE_LOAD',
            data: true,
          })
          dispatch({ type: 'SET_STEP_NO', data: state.stepNo })

          if (
            activeLink !== null &&
            activeLink !== undefined &&
            state.stepNo !== 0
          ) {
            if (
              activeLink === ActiveLinkTypeDnaplate.OligoConfiguration &&
              state.stepNo >= 1
            ) {
              router.push(
                `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
                  ActiveLinkTypeDnaplate.OligoConfiguration
                }`
              )
            } else if (
              activeLink === ActiveLinkTypeDnaplate.Specifications &&
              state.stepNo >= 2
            ) {
              router.push(
                `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
                  ActiveLinkTypeDnaplate.Specifications
                }`
              )
            } else if (
              activeLink === ActiveLinkTypeDnaplate.PackingAndShipping &&
              state.stepNo >= 3
            ) {
              router.push(
                `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
                  ActiveLinkTypeDnaplate.PackingAndShipping
                }`
              )
            } else if (
              (activeLink === ActiveLinkTypeDnaplate.OrderConfirmation &&
                state.stepNo >= 4) ||
              (productDetails.productType === PlateProduct.DuplexPlate &&
                activeLink === ActiveLinkTypeDnaplate.OrderConfirmation &&
                state.stepNo >= 3)
            ) {
              router.push(
                `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
                  ActiveLinkTypeDnaplate.OrderConfirmation
                }`
              )
            } else {
              router.push(
                `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
                  ActiveLinkTypeDnaplate.SequenceUpload
                }`
              )
            }
          }
        }
      } else {
        dispatch({
          type: 'IS_PAGE_LOAD',
          data: true,
        })
        router.push(
          `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
            ActiveLinkTypeDnaplate.SequenceUpload
          }`
        )
      }
      sessionStorage.getItem('CONFIGURATOR_REFERRER_ID') !== null &&
        sessionStorage.removeItem('CONFIGURATOR_REFERRER_ID')
    }
  }, [sessionResp])

  React.useEffect(() => {
    setActiveStep(state.stepNo)

    if (state.stepNo === 0) {
      setEnableOligoConfigurationLink(true)
      setEnableSpecificationsLink(true)
      setEnablePackingAndShippingLink(true)
      setEnableOrderConfirmationLink(true)
    } else if (state.stepNo === 1) {
      setEnableOligoConfigurationLink(false)
      setEnableSpecificationsLink(true)
      setEnablePackingAndShippingLink(true)
      setEnableOrderConfirmationLink(true)
    } else if (state.stepNo === 2) {
      setEnableOligoConfigurationLink(false)
      setEnableSpecificationsLink(false)
      setEnablePackingAndShippingLink(true)
      setEnableOrderConfirmationLink(true)
    } else if (state.stepNo === 3) {
      if (productDetails.productType !== PlateProduct.DuplexPlate) {
        setEnableOligoConfigurationLink(false)
        setEnableSpecificationsLink(false)
        setEnablePackingAndShippingLink(false)
        setEnableOrderConfirmationLink(true)
      } else {
        setEnableOligoConfigurationLink(false)
        setEnableSpecificationsLink(false)
        setEnableOrderConfirmationLink(false)
      }
    } else if (state.stepNo === 4) {
      setEnableOligoConfigurationLink(false)
      setEnableSpecificationsLink(false)
      setEnablePackingAndShippingLink(false)
      setEnableOrderConfirmationLink(false)
    }
  }, [state.stepNo])

  React.useEffect(() => {
    !state.pageError.length ? setOpen(false) : setOpen(true)
  }, [state.pageError])

  React.useEffect(() => {
    return function cleanupError() {
      !loggedIn
        ? sessionStorage.setItem(
            'CONFIGURATOR_REFERRER_ID',
            productDetails.participantId
          )
        : sessionStorage.removeItem('CONFIGURATOR_REFERRER_ID')
    }
  }, [])

  if (!router || !router.query) return null
  let { activeLink = '' } = router.query
  activeLink = validateActiveLink(activeLink)

  const breadcrumbItems = [
    {
      url: homeRoute.index(),
      label: <FormattedMessage {...messages.HOME} />,
    },
    {
      url: BreadcrumbNavigationUrls.BreadcrumbCustomProducts,
      label: (
        <FormattedMessage {...messages[`CONF_CUSTOM_PRODUCTS_${product}`]} />
      ),
    },
    {
      url: BreadcrumbNavigationUrls[`Breadcrumb_${product}`],
      label: (
        <FormattedMessage {...messages[`CONF_OLIGOS_PROBES_${product}`]} />
      ),
    },
    { label: <FormattedMessage {...messages[`CONF_PLATES_${product}`]} /> },
  ]

  const sidebarItems = [
    {
      label: <FormattedMessage {...messages.CONF_DNAPLATES_SEQUENCE_UPLOAD} />,
      activeLink:
        activeLink === ActiveLinkTypeDnaplate.SequenceUpload ? true : false,
      url: `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
        ActiveLinkTypeDnaplate.SequenceUpload
      }`,
      linkDisable: false,
    },
    {
      label: (
        <FormattedMessage {...messages.CONF_DNAPLATES_OLIGO_CONFIGURATION} />
      ),
      activeLink:
        activeLink === ActiveLinkTypeDnaplate.OligoConfiguration ? true : false,
      url: `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
        ActiveLinkTypeDnaplate.OligoConfiguration
      }`,
      linkDisable: enableOligoConfigurationLink,
    },
    {
      label: <FormattedMessage {...messages.CONF_DNAPLATES_SPECIFICATION} />,
      activeLink:
        activeLink === ActiveLinkTypeDnaplate.Specifications ? true : false,
      url: `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
        ActiveLinkTypeDnaplate.Specifications
      }`,
      linkDisable: enableSpecificationsLink,
    },
  ]
  if (PlateProduct.DnaPlate === product) {
    sidebarItems.push({
      label: <FormattedMessage {...messages.CONF_DNAPLATES_PACKING_SHIPPING} />,
      activeLink:
        activeLink === ActiveLinkTypeDnaplate.PackingAndShipping ? true : false,
      url: `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
        ActiveLinkTypeDnaplate.PackingAndShipping
      }`,
      linkDisable: enablePackingAndShippingLink,
    })
  }
  sidebarItems.push({
    label: <FormattedMessage {...messages.CONF_DNAPLATES_ORDER_CONFIRMATION} />,
    activeLink:
      activeLink === ActiveLinkTypeDnaplate.OrderConfirmation ? true : false,
    url: `${dnaPlatesRoute.index()}?product=${product}&activeLink=${
      ActiveLinkTypeDnaplate.OrderConfirmation
    }`,
    linkDisable: enableOrderConfirmationLink,
  })
  const addProductsFromSideNav = () => {
    // Added comment for avoid es-lint error
  }
  const setActive = () => {
    // Added comment for avoid es-lint error
  }
  const plateContentComponent = (getSpecifications) => {
    if (activeLink === ActiveLinkTypeDnaplate.SequenceUpload)
      return <Step1 productDetails={productDetails} />

    if (activeLink === ActiveLinkTypeDnaplate.OligoConfiguration)
      return (
        <Step2
          specificationsData={getSpecifications}
          productDetails={productDetails}
        />
      )

    if (activeLink === ActiveLinkTypeDnaplate.Specifications)
      return (
        <Step3
          specificationsData={getSpecifications}
          productDetails={productDetails}
        />
      )

    if (activeLink === ActiveLinkTypeDnaplate.PackingAndShipping)
      return (
        <Step4
          specificationsData={getSpecifications}
          productDetails={productDetails}
        />
      )

    if (activeLink === ActiveLinkTypeDnaplate.OrderConfirmation)
      return (
        <Step5
          productDetails={productDetails}
          configuratorAddToMiniCart={configuratorAddToMiniCart}
        />
      )
    return null
  }

  return (
    <configContext.Provider value={{ state, dispatch }}>
      <SpecificationsComponent
        ssr={false}
        variables={{
          product: productDetails,
        }}
      >
        {({ loading, error, data }) => {
          if (loading) return null
          if (error || !data || !data.getSpecifications) return null
          const { getSpecifications } = data
          return (
            <DefaultLayout spacing={LayoutSpacingOptions.MD}>
              {productCode !== 0 ? (
                <ConfiguratorPageError
                  productCode={productCode}
                  country={countryCd}
                />
              ) : (
                <Container>
                  <Breadcrumb items={breadcrumbItems} />

                  <PageHeader
                    label={
                      <FormattedMessage
                        {...messages[
                          `CONF_DNAPLATES_CUSTOM_DNA_PLATES_${product}`
                        ]}
                      />
                    }
                  />
                  <Grid container spacing={10}>
                    <Grid item xs={3}>
                      <LeftNav
                        sidebarItems={sidebarItems}
                        productType={product}
                        addProductsFromSideNav={addProductsFromSideNav}
                        setActive={setActive}
                        activeStep={activeStep}
                        productDetails={productDetails}
                      />
                    </Grid>
                    <Grid item xs={9}>
                      <ErrorAlert
                        open={open}
                        setOpen={setOpen}
                        message={state.pageError}
                      />
                      <div>{plateContentComponent(getSpecifications)}</div>
                    </Grid>
                  </Grid>
                </Container>
              )}
            </DefaultLayout>
          )
        }}
      </SpecificationsComponent>
    </configContext.Provider>
  )
}

export default StandardTube
